import { useState, useEffect, useRef } from 'react';

export interface LinkPreviewData {
  url: string;
  title: string;
  description: string;
  image: string;
  siteName: string;
}

const URL_REGEX = /https?:\/\/[^\s]+/g;

const extractUrl = (text: string): string | null => {
  const matches = text.match(URL_REGEX);
  return matches ? matches[0] : null;
};

// We use allorigins as a CORS proxy to fetch OG meta tags
const fetchPreview = async (url: string): Promise<LinkPreviewData | null> => {
  try {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 5000);
    const proxy = `https://api.allorigins.win/get?url=${encodeURIComponent(url)}`;
    const res = await fetch(proxy, { signal: controller.signal });
    clearTimeout(timeout);
    if (!res.ok) return null;
    const data = await res.json();
    const html: string = data.contents || '';

    const getMeta = (prop: string): string => {
      const m =
        html.match(new RegExp(`<meta[^>]*property=["']og:${prop}["'][^>]*content=["']([^"']+)["']`, 'i')) ||
        html.match(new RegExp(`<meta[^>]*content=["']([^"']+)["'][^>]*property=["']og:${prop}["']`, 'i')) ||
        html.match(new RegExp(`<meta[^>]*name=["']${prop}["'][^>]*content=["']([^"']+)["']`, 'i'));
      return m ? m[1] : '';
    };

    const titleMatch = html.match(/<title[^>]*>([^<]+)<\/title>/i);
    const title = getMeta('title') || (titleMatch ? titleMatch[1] : '') || url;
    const description = getMeta('description');
    const image = getMeta('image');
    const siteName = getMeta('site_name') || new URL(url).hostname.replace('www.', '');

    return { url, title: title.slice(0, 100), description: description.slice(0, 200), image, siteName };
  } catch {
    return null;
  }
};

export const useLinkPreview = (text: string) => {
  const [preview, setPreview] = useState<LinkPreviewData | null>(null);
  const [loading, setLoading] = useState(false);
  const lastUrl = useRef<string>('');
  const timer = useRef<any>(null);

  useEffect(() => {
    const url = extractUrl(text);

    if (!url) {
      setPreview(null);
      lastUrl.current = '';
      return;
    }

    if (url === lastUrl.current) return;

    clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      lastUrl.current = url;
      setLoading(true);
      const data = await fetchPreview(url);
      setPreview(data);
      setLoading(false);
    }, 800); // debounce

    return () => clearTimeout(timer.current);
  }, [text]);

  const dismiss = () => {
    setPreview(null);
    lastUrl.current = '__dismissed__';
  };

  return { preview, loading, dismiss };
};